import React, { useEffect, useRef, useState } from 'react';
import { ChevronDownIcon } from '../icons';
import { apiService, ContactUsage, contactPagePath } from '../../services/apiService';
import { trackClick } from '../../utils/analytics';
import { ContactLimitNotice, ContactUsageLine, ContactBlockReason } from './ContactLimitNotice';

/**
 * Contact university — the addresses, not a message composer.
 *
 * We are not making introductions on anyone's behalf, so the useful thing is
 * the university's own address, copyable, plus the concierge if they would
 * rather we helped. The old compose-and-send ContactUniversityModal is still
 * in the tree behind USE_TTO_CONTACT_MODAL in App.tsx.
 *
 * Addresses are grouped into two tiles (2026-09-20 design): the named
 * TECH TRANSFER MANAGER(s) on this technology, and the TECH TRANSFER OFFICE —
 * the curated `tto_office_details` when the university has one, else the
 * scraped licensing office plus the catch-all `common_email`. A tile with
 * nothing in it is not rendered rather than shown empty. Each tile's Copy
 * puts every address on the clipboard as "a@x, b@x" — the form a mail
 * client's To field accepts as-is — and a tile with several addresses
 * expands to one line per address with its own copy control.
 *
 * INTEREST TRACKING: someone who looks up a TTO address is a warm lead even
 * if they never book the call. Every opening is recorded by the server from
 * the contacts request itself, which carries `surface` (where the sheet was
 * opened) and the page path (Admin -> Leads -> Contact University). This
 * sheet used to post a `contact_university` lead of its own as it opened; it
 * no longer does (2026-10-08). The Talk to Concierge click is still posted
 * from here, as a `talk_to_concierge` lead with the technology, university,
 * surface and page (Admin -> Leads -> Talk to Concierge). Whether either
 * e-mails the team is an admin switch per kind (Backend/cta_alerts.py).
 *
 * CONTACT LIMIT (2026-10): the addresses come from the metered
 * /technologies/{id}/contacts. Within a limit a line under the header says how
 * many have been used; past it the server sends usage instead of addresses
 * and ContactLimitNotice (with "Request more") takes the tiles' place. The
 * concierge section stays either way.
 */

/** Google's booking page sends X-Frame-Options: SAMEORIGIN — verified, including
 *  with ?gv=true — so it cannot be embedded and has to open in a new tab. */
// Single source of truth — "Book a Demo" opens this same calendar.
import { CONCIERGE_BOOKING_URL } from '../../config/booking';
import { recordInterest } from '../../utils/interest';

interface TTOAddress {
    email: string;
    /** The person behind a manager address; offices carry no name here. */
    name?: string;
}

interface TTOTile {
    key: 'manager' | 'office';
    label: string;
    addresses: TTOAddress[];
}

/** What a mail client's To/Cc field accepts pasted as-is. */
const joinForMail = (addresses: TTOAddress[]) => addresses.map(a => a.email).join(', ');

/** Every address inside a string, wherever it sits ("a@x, b@x", "[Name](mailto:a@x)").
 *  Requires a dotted domain, so Cloudflare's "[email protected]" stub matches nothing. */
const ADDRESS = /[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+/g;

/** The endpoint hands JSON columns back parsed today, but tolerate a JSON
 *  string too; an unparseable string is treated as nothing, not as a value. */
const asJson = (v: any) => {
    if (typeof v !== 'string' || !v.trim()) return v;
    try { return JSON.parse(v); } catch { return null; }
};

interface ContactTTOModalProps {
    isOpen: boolean;
    onClose: () => void;
    techId: string;
    techTitle: string;
    university?: string;
    /** Used only to attribute the lead; never shown in this modal. */
    userName?: string;
    userEmail?: string;
    /** Where the sheet was opened (search_results, tech_modal, tech_page …):
     *  sent with the contacts request and the Talk to Concierge lead. */
    surface?: string;
}

const CopyIcon: React.FC = () => (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <rect x="9" y="9" width="11" height="11" rx="2" />
        <path d="M5 15V5a2 2 0 0 1 2-2h10" strokeLinecap="round" />
    </svg>
);

export const ContactTTOModal: React.FC<ContactTTOModalProps> = ({
    isOpen, onClose, techId, techTitle, university, userName, userEmail, surface,
}) => {
    const [tiles, setTiles] = useState<TTOTile[]>([]);
    const [loading, setLoading] = useState(true);
    // What was last copied — a tile key ("manager") or one address — so the
    // control that did it can say "Copied" without the others echoing it.
    const [copied, setCopied] = useState('');
    const [expanded, setExpanded] = useState<Record<string, boolean>>({});
    const [error, setError] = useState('');
    // Contact-limit state from the server: usage while within the limit
    // (null = unlimited or an older backend), `blocked` once past it.
    const [usage, setUsage] = useState<ContactUsage | null>(null);
    const [blocked, setBlocked] = useState<{ reason: ContactBlockReason; usage: ContactUsage | null } | null>(null);
    const [needsSignIn, setNeedsSignIn] = useState(false);
    // One "Copied" reset timer, so a second copy within 1.8s keeps its own
    // feedback and a timer from a previous opening cannot blank a fresh one.
    const copyTimer = useRef<number | undefined>(undefined);

    /** Record the interest, with what was clicked and where (the team alert
     *  is built from these). `message` and `areas` stay as they were: older
     *  backends read the technology and university from them. Best-effort:
     *  never block or fail the modal. */
    const logInterest = (source: string, note: string) => {
        if (!userEmail) return;
        apiService.createConciergeCall({
            name: userName || userEmail,
            email: userEmail,
            areas: [university || 'University'],
            message: note,
            form_source: source,
            tech_id: techId || undefined,
            tech_title: techTitle || undefined,
            university: university || undefined,
            surface: surface || undefined,
            page_url: contactPagePath() || undefined,
        }, apiService.getStoredUserId() || undefined).catch(() => { /* best effort */ });
    };

    useEffect(() => {
        if (!isOpen || !techId) return;
        let cancelled = false;
        setLoading(true); setError(''); setCopied('');
        setUsage(null); setBlocked(null); setNeedsSignIn(false);

        (async () => {
            try {
                // The server records this opening (and where it was) itself.
                const res = await apiService.getTechnologyContacts(techId, { surface, purpose: 'open' });
                if (cancelled) return;
                if (res.status === 401) {
                    // No session, or one that could not be renewed: the
                    // contacts are for signed-in accounts only.
                    setNeedsSignIn(true);
                    setTiles([]);
                    return;
                }
                if (!res.success || !res.data) {
                    setError('Could not load the contact details for this technology.');
                    setTiles([]);
                    return;
                }
                if (!res.data.allowed) {
                    setBlocked({ reason: res.data.reason || 'limit_reached', usage: res.data.usage });
                    setTiles([]);
                    return;
                }
                setUsage(res.data.usage);
                // Same four fields the details endpoint used to carry.
                const contacts: any = res.data.contacts || {};
                // De-dupe by address across BOTH tiles: the named manager,
                // the office and the catch-all are often the same mailbox, and
                // one address in two tiles reads as a bug. The OFFICE claims
                // first: for MIT, Purdue and a dozen others the only
                // "manager" row is the office inbox itself, and labelling
                // tlo@mit.edu a person while hiding the office tile is wrong
                // both ways. A real person with their own address survives.
                //
                // `take` EXTRACTS addresses rather than trusting the string:
                // a scraped `email` value is often "a@x, b@x" (950 Agrinnovate
                // rows, 3.3k tech_manager_default rows), a Cloudflare
                // "[email protected]" artefact (268 rows) or markdown like
                // "[Name](mailto:a@x)". Pulling out only what looks like an
                // address de-dupes each part properly, drops the junk, and
                // rescues the real address from the wrapper.
                const seen = new Set<string>();
                const take = (value: any, name?: any): TTOAddress[] => {
                    const person = typeof name === 'string' ? name.trim() : '';
                    const found = typeof value === 'string' ? (value.match(ADDRESS) || []) : [];
                    const out: TTOAddress[] = [];
                    for (const addr of found) {
                        const k = addr.toLowerCase();
                        if (seen.has(k)) continue;
                        seen.add(k);
                        out.push(person ? { email: addr, name: person } : { email: addr });
                    }
                    return out;
                };

                // Curated tech-transfer office (technologies.tto_office_details)
                // from the reviewed Global Universities sheet; NULL means not
                // curated yet. The endpoint may hand JSON columns back as
                // strings, so accept an object, a JSON string, or nothing. When
                // it exists it IS the office — the scraped tech_manager_default
                // is the university's own name for 7 universities and the
                // legacy common_email can be stale — so the fallbacks are only
                // consulted for universities nobody has curated.
                const ttoOffice = asJson(contacts.tto_office_details);
                const curated: string[] = Array.isArray(ttoOffice?.emails)
                    ? ttoOffice.emails.filter((e: any) => typeof e === 'string' && e.trim())
                    : [];
                let officeAddrs: TTOAddress[];
                if (curated.length) {
                    officeAddrs = curated.flatMap(e => take(e));
                } else {
                    const office = asJson(contacts.tech_manager_default);
                    // common_email is one string that sometimes carries several
                    // addresses split by "," or ";" — `take` splits it.
                    officeAddrs = [...take(office?.email), ...take(contacts.common_email)];
                }

                const raw = asJson(contacts.tech_manager_details);
                const managers = Array.isArray(raw) ? raw : raw ? [raw] : [];
                const managerAddrs = managers.flatMap((m: any) => take(m?.email, m?.name));

                // A tile with nothing in it is not shown at all.
                setTiles(([
                    { key: 'manager', label: 'Tech Transfer Manager', addresses: managerAddrs },
                    { key: 'office', label: 'Tech Transfer Office', addresses: officeAddrs },
                ] as TTOTile[]).filter(t => t.addresses.length > 0));
                setExpanded({});
            } catch {
                if (!cancelled) { setError('Could not load the contact details.'); setTiles([]); }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, techId]);

    useEffect(() => {
        if (!isOpen) window.clearTimeout(copyTimer.current);
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    /** Put text on the clipboard and remember which control did it. */
    const copyText = async (text: string, marker: string, what: string) => {
        trackClick('tto_click_copy_email', { what, count: text.split(', ').length, tech_id: techId });
        try {
            await navigator.clipboard.writeText(text);
            setCopied(marker);
            window.clearTimeout(copyTimer.current);
            copyTimer.current = window.setTimeout(() => setCopied(''), 1800);
        } catch {
            // Clipboard needs a secure context and permission; when it is
            // refused, offer the text rather than claiming "Copied".
            setCopied('');
            window.prompt('Copy:', text);
        }
    };

    // "12 of 50 university contacts used …" — only while a limit applies.
    const showUsage = !loading && !blocked && !!usage && usage.limit !== null;

    const talkToConcierge = () => {
        trackClick('tto_click_talk_to_concierge', { tech_id: techId });
        logInterest('talk_to_concierge',
            `Clicked Talk to Concierge for "${techTitle}" (${techId})`);
        recordInterest('concierge', { tech_id: techId || null });
        window.open(CONCIERGE_BOOKING_URL, '_blank', 'noopener,noreferrer');
    };

    return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex justify-center items-center z-50 p-4"
             onClick={onClose}>
            <div role="dialog" aria-modal="true" aria-labelledby="contact-tto-title"
                 className="relative bg-white rounded-2xl shadow-lg w-full max-w-md p-6 flex flex-col max-h-[90vh] overflow-y-auto"
                 onClick={e => e.stopPropagation()}>
                {/* CloseIcon draws white strokes: on the old light-grey chip the
                    x was all but invisible. The brand gold, on its own tint. */}
                <button type="button" onClick={onClose} aria-label="Close"
                    className="absolute top-4 right-4 flex w-9 h-9 justify-center items-center rounded-full bg-primary-100 text-primary-700 hover:bg-primary-200">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
                    </svg>
                </button>

                <h2 id="contact-tto-title" className="text-grayscale-1300 font-grotesk text-xl font-bold pr-10">
                    Contact university
                </h2>
                {university && (
                    <p className={`text-grayscale-1100 font-grotesk text-sm mt-0.5 ${showUsage ? '' : 'mb-4'}`}>{university}</p>
                )}
                {showUsage && <ContactUsageLine usage={usage} className="mt-1 mb-4" />}

                {loading && (
                    <div className="text-grayscale-1100 font-grotesk text-sm py-6">Loading contact details…</div>
                )}
                {!loading && needsSignIn && (
                    <div className="text-grayscale-1200 font-grotesk text-sm py-2">
                        Please sign in to see this university's contact details.
                    </div>
                )}
                {!loading && error && (
                    <div className="text-red-600 font-grotesk text-sm py-2">{error}</div>
                )}
                {!loading && blocked && (
                    <ContactLimitNotice key={techId} reason={blocked.reason} usage={blocked.usage}
                        userName={userName} techId={techId} />
                )}
                {!loading && !error && !blocked && !needsSignIn && tiles.length === 0 && (
                    <div className="text-grayscale-1200 font-grotesk text-sm py-2">
                        No published contact address for this technology yet — our concierge
                        can reach the office on your behalf.
                    </div>
                )}

                {!loading && tiles.length > 0 && (
                    <div className="flex flex-col gap-3">
                        {tiles.map(t => {
                            const many = t.addresses.length > 1;
                            const open = many && !!expanded[t.key];
                            const first = t.addresses[0];
                            return (
                                <div key={t.key}
                                     className={`px-4 py-3 rounded-xl border ${
                                         t.key === 'manager' ? 'border-dashed border-primary-400' : 'border-grayscale-300'}`}>
                                    <div className="flex items-center justify-between gap-3">
                                        <div className="text-grayscale-1100 font-grotesk text-[10px] font-semibold tracking-wider uppercase">
                                            {t.label}
                                        </div>
                                        {/* The tile's Copy takes every address, in the
                                            form a To field accepts, so one click is enough
                                            to write to the whole office. */}
                                        <button type="button"
                                            onClick={() => copyText(joinForMail(t.addresses), t.key, t.key)}
                                            aria-label={copied === t.key ? 'Copied'
                                                : `Copy ${many ? 'all ' + t.addresses.length + ' addresses' : first.email}`}
                                            title={many ? `Copy all ${t.addresses.length} addresses` : 'Copy address'}
                                            className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-primary-400 text-primary-700 font-grotesk text-xs font-medium hover:bg-primary-100/40">
                                            <CopyIcon />
                                            <span aria-live="polite">{copied === t.key ? 'Copied' : 'Copy'}</span>
                                        </button>
                                    </div>

                                    {!many ? (
                                        <div className="mt-2 min-w-0">
                                            {first.name && (
                                                <div className="text-grayscale-1100 font-grotesk text-[11px]">{first.name}</div>
                                            )}
                                            <a href={`mailto:${first.email}`}
                                               className="text-grayscale-1300 font-grotesk text-sm break-all hover:underline">
                                                {first.email}
                                            </a>
                                        </div>
                                    ) : (
                                        <>
                                            {/* ONE toggle that stays mounted in both states, so
                                                keyboard focus survives the flip and aria-expanded
                                                is the real state. Collapsed it reads "first@x
                                                +N more"; open it reads "Show less". */}
                                            <button type="button"
                                                onClick={() => setExpanded(x => ({ ...x, [t.key]: !open }))}
                                                aria-expanded={open} aria-controls={`tto-${t.key}-list`}
                                                className="mt-2 w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-alternate-bg border border-grayscale-200 text-left hover:border-primary-300">
                                                <span className="min-w-0 truncate font-grotesk text-sm">
                                                    {open ? (
                                                        <span className="text-grayscale-1100">Show less</span>
                                                    ) : (
                                                        <>
                                                            <span className="text-grayscale-1300">{first.email}</span>
                                                            <span className="ml-2 text-primary-700 font-medium">+{t.addresses.length - 1} more</span>
                                                        </>
                                                    )}
                                                </span>
                                                <ChevronDownIcon className={`w-4 h-4 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
                                            </button>
                                            {open && (
                                                <div id={`tto-${t.key}-list`} className="mt-2 flex flex-col">
                                                    {t.addresses.map(a => (
                                                        <div key={a.email}
                                                             className="flex items-center justify-between gap-2 py-1.5 border-b border-grayscale-200 last:border-b-0">
                                                            <div className="min-w-0">
                                                                {a.name && (
                                                                    <div className="text-grayscale-1100 font-grotesk text-[11px]">{a.name}</div>
                                                                )}
                                                                <a href={`mailto:${a.email}`}
                                                                   className="text-grayscale-1300 font-grotesk text-sm break-all hover:underline">
                                                                    {a.email}
                                                                </a>
                                                            </div>
                                                            <button type="button" onClick={() => copyText(a.email, a.email, t.key + '_one')}
                                                                aria-label={copied === a.email ? 'Copied' : `Copy ${a.email}`}
                                                                title="Copy this address"
                                                                className="shrink-0 p-1.5 rounded-lg text-grayscale-1100 hover:text-primary-700 hover:bg-primary-100/40">
                                                                {copied === a.email
                                                                    ? <span className="text-[11px] font-grotesk text-primary-700">Copied</span>
                                                                    : <CopyIcon />}
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                <div className="border-t border-grayscale-300 mt-5 pt-4">
                    <h3 className="text-grayscale-1300 font-grotesk text-base font-bold">
                        Talk to Concierge
                    </h3>
                    <p className="text-grayscale-1200 font-grotesk text-sm mt-1 mb-4">
                        With our concierge you will have a dedicated person help support your
                        engagement with the tech transfer office.
                    </p>
                    <button type="button" onClick={talkToConcierge}
                        className="w-full py-3 rounded-full bg-primary-500 text-grayscale-100 font-grotesk text-sm font-medium hover:bg-primary-600">
                        Talk to Concierge
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ContactTTOModal;
